import { Search } from "@mui/icons-material"
import { Divider, Fade, Grid, Input, InputAdornment, TableRow, Typography } from "@mui/material"
import { NavigateFunction } from "react-router-dom"
import { MarketData } from "../../stores/global"
import { Button, ProgressWheel, StyledPaper } from "../commons"
import { CustomizedTablesProps, StyledTableCell } from "../commons/CompactTable"
import { displayNumber } from "../commons/utils"

export const tableHeadersMarket: CustomizedTablesProps['tableHeader'] = [
  // { label: 'Assets', props: { sx: { paddingLeft: '1.5rem' } } }, example of how to add props
  { label: 'Assets', props: { sx: { paddingLeft: '1.5rem' } } },
  { label: 'Price' },
  { label: 'Total Deposited' },
  { label: 'Deposit APY' },
  { label: 'Total Borrowed' },
  { label: 'Borrow APY' },
  { label: 'Utilization Rate' },
  { label: '' }
]

interface SearchBoxProps {
  callback: (value: string) => void
  sx?: any
}

export const SearchBox = (prop: SearchBoxProps) => {
  const { callback, sx } = prop
  return (
    <StyledPaper color='primary' sx={{ ...sx }} >
      <Input type="string" placeholder="Search" sx={{
        '& input::placeholder': {
          fontSize: '1rem',
        },
      }} startAdornment={
        <InputAdornment position="start">
          <Search sx={{ color: 'text.primary', padding: '0.2rem', marginLeft: ' 0.5rem' }} />
        </InputAdornment>
      }
        onChange={(e) => callback(e.target.value)}
      />
    </StyledPaper>
  )
}

export const constructMarketTable = (datas: MarketData[], navigate: NavigateFunction) => {
  const navigateTo = (path: string) => {
    navigate(path)
  }

  return datas.map((data, index) => {
    const { icon: AssetIcon, asset, name, price, totalSupplied, totalSuppliedCap, supplyApy, totalBorrowed, totalBorrowedCap, borrowApy, utilizationRate } = data
    const maxBorrow = totalBorrowedCap ? Math.min(totalBorrowedCap, totalSupplied) : totalSupplied
    // const AssetIcon = icon ? icon : <></>
    const animationTimeout = (2000 * index + 1) > 3000 ? 3000 : 2000 * index + 1
    return (
      <Fade in={true} timeout={animationTimeout}>
        <TableRow>
          <StyledTableCell sx={{ paddingLeft: '1.5rem' }}>
            <Grid container alignItems={'center'} gap={1} flexWrap={'nowrap'}>
              <AssetIcon style={{ width: '2rem' }} />
              <Grid container item flexDirection={'column'}>
                <Typography variant='s'>{asset}</Typography>
                <Typography variant='xs' color='text.secondary'>{name}</Typography>
              </Grid>
            </Grid>
          </StyledTableCell>
          <StyledTableCell align="left">
            <Typography variant='s' component={'div'}>${displayNumber(price, 2, 2)}</Typography>
          </StyledTableCell>
          <StyledTableCell align="left">
            <Grid container gap={1} flexWrap={'nowrap'}>
              <Grid item>
                <ProgressWheel value={totalSuppliedCap ? (totalSupplied / totalSuppliedCap) * 100 : 0} size={35} noText style={{ marginTop: '0.15rem' }} />
              </Grid>
              <Grid item>
                <Typography variant='s' component={'div'}>{displayNumber(totalSupplied, 6, 2)}</Typography>
                <Typography variant='xs' color='text.secondary'>{`$${displayNumber(totalSupplied * price, 2, 2)}`}</Typography>
              </Grid>
            </Grid>
          </StyledTableCell>
          <StyledTableCell align="left">
            <Typography variant='s' component={'div'}>{supplyApy?.toFixed(2)}%</Typography>
          </StyledTableCell>
          <StyledTableCell align="left">
            <Grid container gap={1} flexWrap={'nowrap'}>
              <Grid item>
                <ProgressWheel color='error' value={totalBorrowed ? (totalBorrowed / maxBorrow) * 100 : 0} size={35} noText style={{ marginTop: '0.15rem' }} />
              </Grid>
              <Grid item>
                <Typography variant='s' component={'div'}>{displayNumber(totalBorrowed, 6, 2)}</Typography>
                <Typography variant='xs' color='text.secondary'>{`$${displayNumber(totalBorrowed * price, 2, 2)}`}</Typography>
              </Grid>
            </Grid>
          </StyledTableCell>
          <StyledTableCell align="left">
            <Typography variant='s' component={'div'}>{borrowApy?.toFixed(2)}%</Typography>
          </StyledTableCell>
          <StyledTableCell align="left">
            <Typography variant='s' component={'div'}>{(utilizationRate ?? 0)?.toFixed(2)}%</Typography>
          </StyledTableCell>
          <StyledTableCell align="right">
            <Grid container justifyContent={'flex-end'}>
              <Button variant='contained' color="secondary" onClick={() => navigateTo(`/token/${asset}`)}>
                Details
              </Button>
            </Grid>
          </StyledTableCell>
        </TableRow>
      </Fade>
    )
  })
}

export const constructMarketTableMobile = (datas: MarketData[], navigate: NavigateFunction) => {

  const navigateTo = (path: string) => {
    navigate(path)
  }

  return datas.map((data: any) => {
    const { icon: AssetIcon, asset, name, price, totalSupplied, totalSuppliedCap, supplyApy, totalBorrowed, totalBorrowedCap, borrowApy } = data
    const maxBorrow = totalBorrowedCap ? Math.min(totalBorrowedCap, totalSupplied) : totalSupplied
    return (
      <TableRow>
        <StyledTableCell sx={{ paddingLeft: '1rem', borderTop: '1px solid' }}>
          <Grid container alignItems={'center'} gap={2}>
            <Grid item container alignItems={'center'} gap={1} flexDirection={'row'} flexWrap={'nowrap'}>
              <AssetIcon style={{ minWidth: '2rem' }} />
              <Grid container item flexDirection={'column'}>
                <Typography variant='s'>{asset}</Typography>
                <Typography variant='xs' color='text.secondary'>{name}</Typography>
              </Grid>
              <Typography variant='s' component={'div'}>${displayNumber(price, 2, 2)}</Typography>
            </Grid>
            <Grid item container alignItems={'center'} gap={1} flexDirection={'row'} flexWrap={'nowrap'}>
              <Typography variant='s' noWrap sx={{ width: '100%' }}>
                Total Deposited
              </Typography>
              <Grid container item gap={1} justifyContent={'flex-end'}>
                <Grid item>
                  <ProgressWheel value={totalSuppliedCap ? (totalSupplied / totalSuppliedCap) * 100 : 0} size={25} noText style={{ marginTop: '0.3rem' }} />
                </Grid>
                <Grid item>
                  <Typography variant='s' component={'div'}>${displayNumber(totalSupplied, 2, 2)}</Typography>
                  <Typography variant='xs' color='text.secondary'>{totalSuppliedCap ? `$${displayNumber(totalSuppliedCap, 2, 2)}` : '∞'}</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item container alignItems={'center'} justifyContent={'space-between'} gap={0.5}>
              <Typography variant='s'>
                Deposit APY
              </Typography>
              <Typography variant='s' component={'div'}>{supplyApy?.toFixed(2)}%</Typography>
            </Grid>
            <Divider sx={{ backgroundColor: 'text.secondary', width: '100%' }} />
            <Grid item container alignItems={'center'} gap={1} flexDirection={'row'} flexWrap={'nowrap'}>
              <Typography variant='s' noWrap sx={{ width: '100%' }}>
                Total Borrowed
              </Typography>
              <Grid container item gap={1} justifyContent={'flex-end'}>
                <Grid item>
                  <ProgressWheel color='error' value={totalBorrowed ? (totalBorrowed / maxBorrow) * 100 : 0} size={25} noText style={{ marginTop: '0.3rem' }} />
                </Grid>
                <Grid item>
                  <Typography variant='s' component={'div'}>${displayNumber(totalBorrowed, 2, 2)}</Typography>
                  <Typography variant='xs' color='text.secondary'>{`$${displayNumber(maxBorrow, 2, 2)}`}</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item container alignItems={'center'} justifyContent={'space-between'} gap={0.5}>
              <Typography variant='s'>
                Borrow APY
              </Typography>
              <Typography variant='s' component={'div'}>{borrowApy?.toFixed(2)}%</Typography>
            </Grid>
            <Grid item container alignItems={'center'} justifyContent={'space-between'} gap={0.5}>
              <Grid container justifyContent={'flex-end'}>
                <Button variant='contained' color="secondary" sx={{ width: '100%' }} onClick={() => navigateTo(`/token/${asset}`)}>
                  Details
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </StyledTableCell>
      </TableRow>
    )
  })
}